import * as React from "react";
// MUI
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/GridLegacy";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InsertLinkOutlinedIcon from "@mui/icons-material/InsertLinkOutlined";

// Third Party
import parse from "html-react-parser";
import { useAuth0 } from "@auth0/auth0-react";

// Internal
import { useQuery, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { GET_RELATED_PUBLICATIONS } from "../data/graphql/queries";
import {
  REACT_PUBLICATION,
  COMMENT_PUBLICATION,
} from "../data/graphql/mutations";
import { PublicationType, ReactionTypeEnum, TagType } from "../data/types";
import PublicationCard from "./PublicationCard";
import FeedItemActions from "../pages/community/components/FeedItemActions";
import en from "../data/en.json";

interface PublicationDetailProps {
  publication: PublicationType;
}

export default function PublicationDetail(props: PublicationDetailProps) {
  const { user: auth0User } = useAuth0();
  const { loading, error, data } = useQuery(GET_RELATED_PUBLICATIONS, {
    variables: {
      number_of_publications: 3,
      publication_uuid: props.publication.uuid,
    },
  });

  // Local copy so reactions/comments update in place after a mutation. The
  // mutation only returns those two fields, so we merge them into the
  // publication passed in via props.
  const [reactions, setReactions] = React.useState(
    props.publication.reactions
  );
  const [comments, setComments] = React.useState(props.publication.comments);

  React.useEffect(() => {
    setReactions(props.publication.reactions);
    setComments(props.publication.comments);
  }, [props.publication]);

  const [reactPublication] = useMutation(REACT_PUBLICATION);
  const [commentPublication] = useMutation(COMMENT_PUBLICATION);

  const handleReact = () => {
    if (!auth0User) return;
    reactPublication({
      variables: {
        user_auth0_id: auth0User.sub,
        reaction_type: ReactionTypeEnum.love,
        publication_id: props.publication.id,
      },
    }).then((r) => {
      const updated = r.data?.reactPublication as PublicationType | undefined;
      if (updated) setReactions(updated.reactions);
    });
  };

  const handleComment = (commentText: string) => {
    if (!auth0User) return;
    commentPublication({
      variables: {
        user_auth0_id: auth0User.sub,
        publication_id: props.publication.id,
        comment: commentText,
      },
    }).then((r) => {
      const updated = r.data?.commentPublication as PublicationType | undefined;
      if (updated) setComments(updated.comments);
    });
  };

  if (loading) return <p>{en.common["loading.."]}</p>;
  if (error)
    return (
      <p>
        {en.common.error}: {error.message}
      </p>
    );

  return (
    <React.Fragment>
      <Grid container justifyContent={"center"} sx={{ m: 5, mt: 10 }}>
        <Grid item xs={2} />
        <Grid item xs={8} sx={{ border: 1, p: 5, borderColor: grey[300] }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <Link
              to={`/users/${props.publication.user.handle}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  mb: 3,
                  cursor: "pointer",
                }}
              >
                {props.publication.user.profile.photo?.url && (
                  <Avatar
                    sx={{ m: 1, ml: 0 }}
                    alt="userProfile"
                    src={props.publication.user.profile.photo?.url}
                  />
                )}
                {!props.publication.user.profile.photo?.url && (
                  <Avatar sx={{ bgcolor: "#2B2D6A", m: 1, ml: 0 }}>
                    <AccountCircleIcon />
                  </Avatar>
                )}
                <Typography variant="body2">
                  {props.publication.user.profile.nickname}
                </Typography>
              </Box>
            </Link>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                flexDirection: "row",
              }}
            >
              <Typography variant="h3">
                {parse(props.publication.title)}
              </Typography>
            </Box>

            {props.publication.coverPhotoUrl && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignSelf: "stretch",
                  mt: 3,
                  mb: 3,
                }}
              >
                <img
                  alt="Cover photo"
                  src={props.publication.coverPhotoUrl}
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                />
              </Box>
            )}

            {props.publication.htmlContent && (
              <Box>{parse(props.publication.htmlContent)}</Box>
            )}

            {props.publication.coverMedia?.url && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignSelf: "center",
                }}
              >
                <img
                  alt="coveredMedia"
                  src={props.publication.coverMedia.url}
                  width={650}
                  height={"auto"}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 3,
                pb: 3,
              }}
            >
              {props.publication.tags.map((tag: TagType) => (
                <Chip
                  label={tag.name}
                  key={tag.name}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>

            <Divider flexItem variant="middle" sx={{ color: grey[800] }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 3,
              }}
            >
              <Box sx={{ pr: 2 }}>
                <FacebookOutlinedIcon />
              </Box>
              <Box sx={{ pr: 2 }}>
                <InsertLinkOutlinedIcon />
              </Box>
            </Box>

            <Box
              sx={{
                alignSelf: "stretch",
              }}
            >
              <FeedItemActions
                user={auth0User}
                reactions={reactions}
                comments={comments}
                onReact={handleReact}
                onComment={handleComment}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={2} />
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          pt: 3,
        }}
      >
        <Typography variant="body2">
          {en.publications.relatedPublications}
        </Typography>
      </Box>
      <Grid
        container
        flexDirection="row"
        justifyContent={"center"}
        sx={{ pt: 1 }}
      >
        {data.relatedPublications.map((publication: PublicationType) => (
          <Grid
            key={publication.uuid}
            item
            xs={3}
            sx={{ border: 1, borderColor: grey[300], m: 2, p: 2 }}
          >
            <Link
              to={`/publications/${publication.uuid}`}
              style={{ textDecoration: "none", color: "black" }}
            >
              <PublicationCard
                publication={publication}
                hideCoverMedia={true}
              />
            </Link>
          </Grid>
        ))}
      </Grid>
    </React.Fragment>
  );
}
